var _react_refresh_temp_1;
_react_refresh_temp_1 = $RefreshSig$();
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { translate, useToastService } from '@lucidlink/uicore';
import { usePlugFilespaceClient } from '#query-hooks';
import { GuestLinkKeyFactory } from '../query-key-factories/GuestLinkKeyFactory';
import { GuestsKeyFactory } from '../query-key-factories/GuestsKeyFactory';
export const useDeleteGuestLinkAcl = ({ filespaceId, workspaceId, }) => {
    _react_refresh_temp_1();
    const { plugFilespaceClient } = usePlugFilespaceClient();
    const { addToast } = useToastService();
    const queryClient = useQueryClient();
    const setRemovalOptimistic = (params, isOptimistic) => {
        queryClient.setQueryData(GuestLinkKeyFactory.exact({
            workspaceId,
            filespaceId,
            linkId: params.guestLinkId,
        }), (old) => old && {
            ...old,
            guestLinkAcl: old.guestLinkAcl.map((acl) => acl.email === params.email
                ? { ...acl, isOptimistic }
                : acl),
        });
        queryClient.setQueryData(GuestsKeyFactory.exact({
            email: params.email,
            filespaceId,
            workspaceId,
        }), (old) => old && {
            ...old,
            links: old.links.map((link) => link.id === params.guestLinkId
                ? { ...link, isOptimistic }
                : link),
        });
    };
    const mutation = useMutation({
        mutationKey: GuestLinkKeyFactory.mutations({
            filespaceId,
            workspaceId,
        }),
        mutationFn: async (params) => plugFilespaceClient({
            action: (fsClient) => fsClient.guestLinksManagement().deleteGuestLinkAcl(params),
            filespaceId,
            workspaceId,
        }),
        onMutate: async (params) => {
            await Promise.all([
                queryClient.cancelQueries({
                    queryKey: GuestLinkKeyFactory.exact({
                        workspaceId,
                        filespaceId,
                        linkId: params.guestLinkId,
                    }),
                }),
                queryClient.cancelQueries({
                    queryKey: GuestsKeyFactory.exact({
                        email: params.email,
                        filespaceId,
                        workspaceId,
                    }),
                }),
            ]);
            setRemovalOptimistic(params, true);
        },
        onError: (_, params) => {
            // Undo only this mutation's own mark so a concurrent mutation's
            // optimistic state is left untouched.
            setRemovalOptimistic(params, false);
            addToast({
                'data-testid': 'delete-guest-link-acl-error',
                kind: 'negative',
                content: {
                    // The toast content is already PII-masked by ToastContent.
                    message: translate('Failed to remove guest {{email}} from the link', { email: params.email }),
                },
            });
        },
        onSettled: (_data, _error, params) => {
            const pendingMutations = queryClient.isMutating({
                mutationKey: GuestLinkKeyFactory.mutations({
                    filespaceId,
                    workspaceId,
                }),
            });
            // Let the last pending guest-link mutation do all the refetching:
            // an `exact` refetch now would wipe its optimistic ACL state, and
            // per-settle `list` refetches would be redundant.
            if (pendingMutations > 1) {
                return undefined;
            }
            return Promise.all([
                queryClient.invalidateQueries({
                    queryKey: GuestLinkKeyFactory.list({
                        filespaceId,
                        workspaceId,
                    }),
                }),
                queryClient.invalidateQueries({
                    queryKey: GuestLinkKeyFactory.exact({
                        workspaceId,
                        filespaceId,
                        linkId: params.guestLinkId,
                    }),
                }),
                queryClient.invalidateQueries({
                    queryKey: GuestsKeyFactory.all({
                        filespaceId,
                        workspaceId,
                    }),
                }),
            ]);
        },
        networkMode: 'always',
    });
    return mutation;
};
_react_refresh_temp_1(useDeleteGuestLinkAcl, "KrM3mhTkTlZAjWS6DCmlJKC0EYA=", false, () => [usePlugFilespaceClient, useToastService, useQueryClient, useMutation]);
